import { useEffect, useMemo, useState } from "react";

import { usePopover } from "@common/hooks/filtering/ui/SimplePopover";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useAnalyticsDeps } from "@main/pages/Release/Release";
import { useQuery } from "@tanstack/react-query";

interface Props {
  attributeName: string;
}

export function useTestingCoverageSearchPopover({ attributeName }: Props) {
  const { analyticsService } = useAnalyticsDeps();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  const [query, setQuery] = useState<string>("");

  const { open, setOpen, getReferenceProps, reference, getFloatingProps, floating, x, y, strategy } = usePopover({
    placement: "bottom",
  });

  useEffect(() => {
    if (!open) (document.activeElement as HTMLElement)?.blur();
  }, [open]);

  const getDimensionValues = async (attributeName: string, signal?: AbortSignal): Promise<string[]> => {
    if (!selectedWorkspace?.id || !selectedApp?.id) return [];

    const res = await analyticsService.getSearchValues(selectedWorkspace.id, selectedApp.id, attributeName, signal);
    return res.data.values.map((hit) => hit.value);
  };

  const {
    data: availableFilterHits,
    error,
    isLoading: loading,
  } = useQuery(
    ["testingCoveragePopupSearch_" + attributeName, { selectedApp }],
    ({ signal }) => getDimensionValues(attributeName, signal),
    {
      enabled: open,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
    },
  );

  const filteredFilterHits = useMemo(
    () => availableFilterHits?.filter((value) => value.toLowerCase().includes(query.toLowerCase())),
    [availableFilterHits, query],
  );

  return {
    availableFilterHits: filteredFilterHits,
    open,
    setOpen,
    query,
    setQuery,
    error: Boolean(error),
    loading,
    attributeName,
    getFloatingProps,
    floating,
    getReferenceProps,
    reference,
    x,
    y,
    strategy,
  };
}
